<template>
  <form id="arnAntrag">
    <div class="my-4">
      <div class="bgTR px-4 px-lg-5 py-4 py-lg-5">
        <ArnButton class="platziert"/>
        
        <div class="boxTR px-4 px-lg-5 py-3 py-lg-4 w-100">
          <h3 class="pb-3">Bitte Tarif auswählen</h3>
          
          <!-- Tarif-Buttons -->
          <div class="btnbox d-flex justify-content-center align-items-center flex-column flex-lg-row row pb-3">
            <EinzelversicherungButton 
              class="col-12 col-lg-6 pb-3 pb-lg-0"
              :selected="arnsAusgewaehlt"
              :beitragStr="arnsPreisStr"
              @select="toggleARNS"
            />
            <FamilienversicherungButton 
              class="col-12 col-lg-6"
              :selected="arnfAusgewaehlt"
              :beitragStr="arnfPreisStr"
              @select="toggleARNF"
            />
            
            <!-- AOK-Kundenberater Button -->
            <div v-if="shouldShowRdnummer" class="col-12 mt-3">
              <RdnummerLarge />
            </div>
            <div v-if="shouldShowRdnummerInfo" class="col-12 mt-3">
              <RdnummerOk :rdnummer="store.aok" />
            </div>
          </div>
          
          <!-- Hidden Checkbox für Tarif-Validierung (Container sichtbar für Fehlermeldung) -->
          <div class="form-group mb-3">
            <input
              type="checkbox"
              class="form-check-input d-none"
              id="arnTarifAuswahl"
              name="arnTarifAuswahl"
              :checked="tarifAusgewaehlt"
            >
          </div>
          
          <!-- Info-Box: collapsed auf mobile, expanded ab md -->
          <ArnInfoBox ref="infoBoxRef" />
        </div>
        
      </div>
    </div>
    
    <!-- Fehleranzeige bei API-Fehler -->
    <FehleranzeigeKlein 
      v-if="antragIdError" 
      :message="antragIdError" 
      class="mb-3"
    />
    
    <div class="d-flex justify-content-end">
      <OnlineAbschliessen @click="handleWeiter" />
    </div>
    
    <!-- Loading-Anzeige -->
    <p v-if="isGeneratingAntragId" class="text-end text-muted mt-2 font-sm-links">
      <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
      Antragsnummer wird generiert...
    </p>
    
    <p class="text-end text-muted mt-2 font-sm-links">* Pflichtfeld</p>
  </form>
</template>

<script setup>
import { computed, ref, nextTick, onMounted } from 'vue';
import { formValidation } from "@form-validation/bundle/popular";
import { Bootstrap5 } from "@form-validation/plugin-bootstrap5";
import { Trigger } from "@form-validation/plugin-trigger";
import { SubmitButton } from "@form-validation/plugin-submit-button";

import { useRechnerStore } from '@/stores/rechner';
import { useMainStore } from '@/stores/useMainStore.js';
import { useTarifinfosStore } from '@/stores/tarifinfos.js';
import { useAntragStore } from '@/stores/antrag.js';
import { formatiereInitialerBeitrag } from '@/helpers/hilfsfunktionen';

import ArnButton from '@/components/arn/arnbutton.vue';
import ArnInfoBox from '@/components/arn/arninfobox.vue';
import EinzelversicherungButton from '@/components/arn/einzelversicherung_button.vue';
import FamilienversicherungButton from '@/components/arn/familienversicherung_button.vue';
import RdnummerLarge from '@/components/general/rdnummer_large.vue';
import RdnummerOk from '@/components/general/rdnummer_ok.vue';
import OnlineAbschliessen from '@/components/general/online_abschliessen.vue';
import FehleranzeigeKlein from '@/components/general/fehleranzeige_klein.vue';

const store = useRechnerStore();
const mainStore = useMainStore();
const tarifinfosStore = useTarifinfosStore();
const antragStore = useAntragStore();

// Refs
const infoBoxRef = ref(null);

// FormValidation Instanz
let fv = null;

// API Call Status
const isGeneratingAntragId = ref(false);
const antragIdError = ref('');

// Prüft ob RD-Nummer-Dialog (Button) angezeigt werden soll
const shouldShowRdnummer = computed(() => {
  return mainStore.aokEnabled && store.aok === 0;
});

// Prüft ob RD-Nummerninfo angezeigt werden soll
const shouldShowRdnummerInfo = computed(() => {
  return mainStore.aokEnabled && store.aok !== 0;
});

// Tarif-Auswahl Status
const arnsAusgewaehlt = computed(() => store.hatARNS());
const arnfAusgewaehlt = computed(() => store.hatARNF());
const tarifAusgewaehlt = computed(() => arnsAusgewaehlt.value || arnfAusgewaehlt.value);

// Initiale Preise aus API
const arnsPreisStr = computed(() => {
  const preis = tarifinfosStore.getInitialerPreis('ARN', 'ARNS');
  return preis ? formatiereInitialerBeitrag(preis) : 'ab 21,60 €';
});

const arnfPreisStr = computed(() => {
  const preis = tarifinfosStore.getInitialerPreis('ARN', 'ARNF');
  return preis ? formatiereInitialerBeitrag(preis) : 'ab 46,80 €';
});

// Tarif-Toggle Funktionen
const toggleARNS = () => {
  // Nur für die erste Person (ARN hat keine Personen-Iteration in Schritt 0)
  const firstItem = store.items[0];
  if (firstItem) {
    if (firstItem.tarif7 === 1) {
      firstItem.tarif7 = 0;
      firstItem.hasTarif = firstItem.tarif8 === 1;
    } else {
      firstItem.tarif7 = 1;
      // ARNS und ARNF sind exklusiv - nur einer kann gewählt werden
      firstItem.tarif8 = 0;
      firstItem.hasTarif = true;
      // Bei Wechsel zu ARNS: Geburtsdatum der ältesten Person zurücksetzen (nur für ARNF relevant)
      store.geburtsdatumAeltestePerson = '';
    }
    store.calculateAll();
    
    // Revalidate Tarif-Feld
    if (fv) {
      nextTick(() => fv.revalidateField('arnTarifAuswahl'));
    }
  }
};

const toggleARNF = () => {
  const firstItem = store.items[0];
  if (firstItem) {
    if (firstItem.tarif8 === 1) {
      firstItem.tarif8 = 0;
      firstItem.hasTarif = firstItem.tarif7 === 1;
    } else {
      firstItem.tarif8 = 1;
      // ARNS und ARNF sind exklusiv - nur einer kann gewählt werden
      firstItem.tarif7 = 0;
      firstItem.hasTarif = true;
      // ARNF hat nur eine Person (VN) – überzählige Personen aus früherem ARNS-Durchlauf entfernen
      if (store.items.length > 1) {
        const erster = store.items[0];
        store.items = [erster];
        store.setVn(erster); // VN konsistent auf einzige verbleibende Person setzen
      }
    }
    store.calculateAll();
    
    // Revalidate Tarif-Feld
    if (fv) {
      nextTick(() => fv.revalidateField('arnTarifAuswahl'));
    }
  }
};

// Submit-Handler
const handleWeiter = async (event) => {
  event.preventDefault();
  
  // Verhindere mehrfache Klicks während API Call läuft
  if (isGeneratingAntragId.value) {
    return;
  }
  
  if (fv) {
    const button = document.querySelector('[data-shake]');
    fv.validate().then(async (status) => {
      if (status === 'Valid') {
        // Formular ist valide → AntragId generieren
        antragIdError.value = '';
        isGeneratingAntragId.value = true;
        
        try {
          const success = await antragStore.generateAntragId();
          
          if (success && antragStore.hasValidAntragId) {
            // AntragId erfolgreich generiert → Weiter zu Schritt 1
            store.stepStatus = 1;
          } else {
            // Fehler beim Generieren der AntragId
            antragIdError.value = antragStore.nachricht || 'Fehler beim Generieren der Antragsnummer. Bitte versuchen Sie es erneut.';
            console.error('[arn_schritt0] Fehler bei AntragId-Generierung:', antragStore.returnCode, antragStore.nachricht);
            
            if (button) {
              button.classList.add('shake');
              setTimeout(() => button.classList.remove('shake'), 500);
            }
          }
        } catch (error) {
          // Exception abfangen
          antragIdError.value = 'Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut.';
          console.error('[arn_schritt0] Exception bei AntragId-Generierung:', error);
          
          if (button) {
            button.classList.add('shake');
            setTimeout(() => button.classList.remove('shake'), 500);
          }
        } finally {
          isGeneratingAntragId.value = false;
        }
      } else {
        // Formular nicht valide
        if (button) {
          button.classList.add('shake');
          setTimeout(() => button.classList.remove('shake'), 500);
        }
        nextTick(() => {
          const firstInvalid = document.querySelector('#arnAntrag .is-invalid');
          if (firstInvalid) {
            firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        });
      }
    });
  }
};

// InfoBox expanded/collapsed basierend auf Breakpoint
const initInfoBoxState = () => {
  nextTick(() => {
    const accordionCollapse = document.getElementById('flush-collapseOne');
    if (accordionCollapse) {
      // Ab md Breakpoint (768px) standardmäßig expanded
      if (window.innerWidth >= 768) {
        accordionCollapse.classList.add('show');
        const button = document.querySelector('.info-accordion-button');
        if (button) {
          button.classList.remove('collapsed');
          button.setAttribute('aria-expanded', 'true');
        }
      }
    }
  });
};

// Initialisierung
onMounted(() => {
  // Store initialisieren
  store.initializeStore();
  
  // InfoBox State setzen
  initInfoBoxState();
  
  nextTick(() => {
    // Basis-Felder für FormValidation
    const fields = {
      arnTarifAuswahl: {
        validators: {
          callback: {
            message: 'Bitte wählen Sie einen Tarif aus.',
            callback: () => tarifAusgewaehlt.value
          }
        }
      }
    };
    
    // FormValidation initialisieren
    fv = formValidation(document.getElementById('arnAntrag'), {
      fields,
      plugins: {
        bootstrap5: new Bootstrap5({
          rowSelector: '.form-group, .form-check, .form-check-inline'
        }),
        submitButton: new SubmitButton(),
        trigger: new Trigger({
          event: 'blur change',
        }),
      },
    });
  });
});
</script>

<style scoped>
.introclaim {
    margin-bottom: 1.5rem;
}

.platziert {
    position: absolute;
    right: 0;
    top: 0;
    transform: translate(-20%, -50%);
}

.schritt-zaehler-position {
    position: absolute;
    top: 0;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 90vw;
}

@media (min-width: 768px) {
    .schritt-zaehler-position {
        width: 80vw;
    }
}

@media (min-width: 992px) {
    .schritt-zaehler-position {
        width: 70vw;
    }
}

@media (min-width: 1200px) {
    .schritt-zaehler-position {
        width: 60vw;
    }
}

h3 {
    color: #000;
    font-family: "Source Sans Pro";
    font-size: 22px;
    font-style: normal;
    font-weight: 700;
    line-height: 160%;
}
</style>
